<script lang="ts">
import "@cisco-u/base-card/cu-base-card.js";
import "@cisco-u/button/cu-button.js";
import "@cisco-u/promo-card/cu-promo-card.js";
import "@cisco-u/icons/icons/cu-icon-chevron-right.js";
import "@cisco-u/icons/icons/cu-icon-line-right.js";
import "@cisco-u/icons/icons/cu-icon-close-x.js";
import "@cisco-u/bookmark/cu-bookmark.js";
import "@cisco-u/pagination/cu-pagination.js";
import "@cisco-u/carousel/cu-carousel.js";

import { defineComponent } from "vue";
import { mapGetters, mapMutations } from "vuex";

import { Result } from "@/components/Result";
import theCards from "@/components/theCards.vue";
import { EventBus } from "@/store/eventBus";
import { is_progressRemovable } from "@/utils/contentRemovable";
import { sendButtonCtaTelemetry } from "@/utils/ctaTelemetry";

export default defineComponent({
  emits: ["open"],
  props: {
    myList: {
      type: Array || null,
      default: null,
    },
    boxShowable: {
      type: Boolean,
      default: false,
    },
    modelingLabsEnabled: {
      type: Boolean,
      default: false,
    },
    learningLabsEnabled: {
      type: Boolean,
      default: false,
    },
    gradedLabsEnabled: {
      type: Boolean,
      default: false,
    },
    standaloneLabsEnabled: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    theCards,
  },
  data() {
    return {
      surveyCompleted: false,
      vssWidth: 0,
      hasNewSinglePurchase: false,
      availableFilters: [] as string[],
      filterTypes: {
        inProgress: "in-progress",
        subscriptions: "subscriptions",
        bookmarks: "bookmarks",
        assignments: "assignments",
      },
      filterLabels: {
        "in-progress": "In progress",
        subscriptions: "Subscriptions",
        bookmarks: "Bookmarks",
        assignments: "Assignments",
      } as Record<string, string>,
      assignmentText: "Assignments",
      scenario: "",
      viewAllText: "View all in My Dashboard",
    };
  },
  mounted() {
    this.setAvailableFilters();
    this.setInitialFilter();
    this.getWindowSize();
    globalThis.addEventListener("resize", this.getWindowSize);
    EventBus.on("updatesurveyCompleted", (data: boolean) => {
      this.surveyCompleted = data;
    });
  },
  unmounted() {
    globalThis.removeEventListener("resize", this.getWindowSize);
  },
  watch: {
    myList() {
      this.setAvailableFilters();
      this.setInitialFilter();
    },

    hasNewSinglePurchase(a: any) {
      if (!this.myLearningFilter.length && a) {
        this.addMyLearningFilter(this.filterTypes.subscriptions);
      }
    },
    status() {
      this.setInitialFilter();
    },
    availableFilters() {
      if (this.myLearningFilter?.length) {
        this.resetFilter();
      }
    },
  },
  computed: {
    ...mapGetters({
      status: "user/status",
      myLearningFilter: "user/myLearningFilter",
      customerAdminRole: "user/customerAdminRole",
    }),
    orderedFilters(): string[] {
      const filters = Array.isArray(this.myLearningFilter)
        ? this.myLearningFilter
        : Object.values(this.myLearningFilter ?? {});
      const active = filters.filter((f: string) =>
        this.availableFilters.includes(f)
      );
      const inactive = this.availableFilters.filter(
        (f: string) => !filters.includes(f)
      );
      return [...active, ...inactive];
    },
    myListItems(): any[] {
      const list = this.myList ?? [];

      if (
        this.modelingLabsEnabled &&
        this.learningLabsEnabled &&
        this.gradedLabsEnabled &&
        this.standaloneLabsEnabled
      ) {
        return list;
      }

      const disabledLabTypes = new Set(
        [
          !this.modelingLabsEnabled && "cisco-modeling-labs",
          !this.learningLabsEnabled && "cisco-learning-labs",
          !this.gradedLabsEnabled && "graded-labs",
          !this.standaloneLabsEnabled && "standalone-labs",
        ].filter(Boolean)
      );

      return list.filter((item: any) => {
        const isLab =
          item.type === "lab" || item.catalog_data?.contentType === "lab";
        if (!isLab) return true;

        const labType = item.catalog_data?.lab_type ?? item.lab_type;
        return !labType || !disabledLabTypes.has(labType);
      });
    },
    slidesPerGroup(): number {
      if (this.vssWidth >= 1440) {
        return 3;
      } else if (this.vssWidth >= 896) {
        return 2;
      }
      return 1;
    },
    filteredList() {
      let list: any[] = this.myListItems;

      if (
        this.boxShowable &&
        this.availableFilters?.indexOf("bookmarks") == -1
      ) {
        list = [
          {
            type: "favorites-promo",
          },
          ...list,
        ];
      }
      if (this.boxShowable && !this.getUser) {
        // prepend object with type of promo
        list = [
          {
            type: "promo",
          },
          ...list,
        ];
      }

      if (this.myLearningFilter?.length) {
        list = this.filterList(list);
      }

      list = list?.filter(
        (item: any) =>
          ["promo", "favorites-promo"].includes(item.type) ||
          item.catalog_data !== undefined ||
          item.path_data !== undefined ||
          item.exam_voucher_data !== undefined ||
          item.track_data !== undefined ||
          item.bundle_data !== undefined ||
          item.lab_data !== undefined
      );

      // remove anything from list without a type
      list = list.filter((item: any) => item.type);

      return list;
    },
    getUser() {
      if (!this.status) return false;

      return this.status.surveyCompleted;
    },

    getUserStatusDescription() {
      let bookmark = false;

      let greetings = {
        name: "",
        descriptions: "",
      };

      this.myListItems?.forEach((element: any) => {
        if (element.bookmarked) {
          bookmark = true;
        }
      });

      if (this.status) {
        if (this.status.loginCount < 2) {
          greetings.name = "Welcome to Cisco U., " + this.status.display_name;
          greetings.descriptions =
            "Get started with tutorials, or scroll down for popular content.";
        } else if (this.status.surveyCompleted) {
          greetings.name = "Nice to see you, " + this.status.display_name;
          greetings.descriptions =
            "Here are content recommendations just for you. What will you learn today?";
        } else {
          if (bookmark) {
            greetings.name = "Welcome back, " + this.status.display_name;
            greetings.descriptions =
              "You're doing great! Select your interests to see content recommendations just for you.";
          } else {
            greetings.name = "Welcome to Cisco U., " + this.status.display_name;
            greetings.descriptions =
              "Scroll down for popular content, or select your interests to see recommendations just for you.";
          }
        }
      }
      return greetings;
    },
  },
  methods: {
    is_progressRemovable,
    ...mapMutations({
      setupToggleShowAnyway: "user/setupToggleShowAnyway",
      setMyLearningFilter: "user/setMyLearningFilter",
      addMyLearningFilter: "user/addMyLearningFilter",
    }),
    getWindowSize() {
      this.vssWidth = globalThis.innerWidth;
    },
    setAvailableFilters() {
      const list = this.myListItems;
      const filters: string[] = [];
      if (list?.some((course: any) => course.percentage > 0))
        filters.push(this.filterTypes.inProgress);

      if (list?.some((course: any) => course.bookmarked))
        filters.push(this.filterTypes.bookmarks);

      if (list?.some((course: any) => course?.is_single))
        filters.push(this.filterTypes.subscriptions);

      if (list?.some((course: any) => course?.inAssigned))
        filters.push(this.filterTypes.assignments);

      this.availableFilters = filters;
    },
    resetFilter() {
      this.myLearningFilter.forEach((filter: string) => {
        if (!this.availableFilters.includes(filter)) {
          this.setMyLearningFilter(filter);
        }
      });
    },
    setInitialFilter() {
      const list = this.myListItems;
      if (
        list?.some(
          (item: any) =>
            item.is_single && item.created_at > this.status?.last_login
        )
      ) {
        this.hasNewSinglePurchase = true;
      } else if (
        this.availableFilters?.includes(this.filterTypes.inProgress) &&
        !this.myLearningFilter.length
      ) {
        this.addMyLearningFilter(this.filterTypes.inProgress);
      }
    },
    // Extend bundle_data type to include 'name'
    resultData(
      result: Result
    ):
      | (Result["catalog_data"] & { name?: string })
      | (Result["path_data"] & { name?: string })
      | (Result["track_data"] & { name?: string })
      | (Result["exam_voucher_data"] & { name?: string })
      | undefined {
      const typeHandlers = {
        catalog: [
          "podcast",
          "video",
          "course",
          "challenge",
          "lab",
          "webinar",
          "instructor-led",
          "practice-exam",
          "tutorial",
        ],
        path: ["path"],
        examVoucher: ["exam-voucher"],
        bundle: ["bundle", "bundles"],
        track: ["track", "video-track"],
      };

      const handlerType = Object.keys(typeHandlers).find((key) =>
        typeHandlers[key as keyof typeof typeHandlers].includes(result.type)
      );

      if (!handlerType) return result as any;

      const dataKey =
        `${handlerType === "examVoucher" ? "exam_voucher" : handlerType}_data` as keyof Result;
      const data = result[dataKey] as any;

      if (!data) return result as any;

      data.assigned_content = this.getAssignedIndex(data);

      if ((handlerType === "bundle" || handlerType === "track") && !data.type) {
        data.type = handlerType === "bundle" ? "bundle" : "track";
      }

      return data;
    },
    getAssignedIndex(result: any) {
      const index =
        this.filteredList?.findIndex((item) => item.guid === result.guid) + 1;
      const isAssigned =
        this.filteredList?.some(
          (item: any) => item.inAssigned && item.guid === result.guid
        ) ?? false;
      return { index: index, isAssigned: isAssigned };
    },
    filterList(list: any[]) {
      return list?.filter((item: any) => {
        const filters = this.myLearningFilter;

        if (
          filters?.indexOf(this.filterTypes.inProgress) !== -1 &&
          item.percentage > 0
        ) {
          return true;
        }

        if (
          filters?.indexOf(this.filterTypes.subscriptions) !== -1 &&
          item.is_single
        ) {
          return true;
        }
        if (filters?.indexOf(this.filterTypes.bookmarks) !== -1) {
          // Check if it's a bundle or has bookmarked property
          const isBundle = item.bundle_data !== undefined;
          const isBookmarked = item.bookmarked;

          return isBookmarked || isBundle;
        }
        if (
          filters?.indexOf(this.filterTypes.assignments) !== -1 &&
          item.inAssigned
        ) {
          return true;
        }
      });
    },
    handleFilters(filter: string) {
      if (filter == "assignments") {
        this.sendTelemetry();
      }
      this.setMyLearningFilter(filter);
    },
    returnFilterBtnType(filter: string) {
      this.updateScenario();
      // If there is only one available filter, we want to show it as a primary button
      if (this.availableFilters.length === 1) {
        return "tertiary";
      }

      // If the filter is not in the myLearningFilter array, it is a secondary button
      if (this.myLearningFilter?.indexOf(filter) === -1) {
        return "secondary";
      }

      // If it is in the myLearningFilter array, it is a primary button
      return "tertiary";
    },
    returnFilterBtnX(filter: string) {
      return this.availableFilters.length === 1 ||
        this.myLearningFilter?.indexOf(filter) !== -1
        ? "close-x"
        : null;
    },
    promoLink() {
      this.$emit("open");
      this.setupToggleShowAnyway(true);
    },
    async sendTelemetry() {
      await sendButtonCtaTelemetry(
        this.$route.fullPath,
        this.assignmentText,
        false,
        this.scenario,
        globalThis.location.origin + "/for-you",
        "",
        "My Learning"
      );
    },
    updateScenario() {
      if (this.myLearningFilter && typeof this.myLearningFilter === "object") {
        let myLearningList = { ...this.myLearningFilter };

        if (Object.values(this.myLearningFilter).includes("assignments")) {
          this.removeByValue(myLearningList);
          this.scenario = Object.values(myLearningList).join(", ");
        } else {
          myLearningList.assignments = "assignments";
          this.scenario = Object.values(myLearningList).join(", ");
        }
      } else {
        console.error("myLearningFilter is not defined or is not an object.");
      }
    },
    isAdminContent(content: any) {
      return this.customerAdminRole && content.inAssigned && !content.is_single;
    },
    removeByValue(filterList: any) {
      Object.keys(filterList).forEach((key) => {
        if (filterList[key] === "assignments") {
          delete filterList[key];
        }
      });
    },
  },
});
</script>

<template>
  <div class="pt-16 md:pt-8">
    <div class="mx-6 xxl:mx-20 xlg:mx-auto xlg:max-w-[100rem]">
      <div class="container">
        <h1
          class="px-10 py-2.5 font-cisco text-3xl font-light text-black-dark dark:text-white-dark"
        >
          {{ getUserStatusDescription.name }}
        </h1>
        <div class="justify-between px-10 pb-10 md:flex">
          <div class="text-xl font-slim text-black-dark dark:text-white-dark">
            {{ getUserStatusDescription.descriptions }}
          </div>
        </div>
      </div>
    </div>

    <cu-carousel
      title="My Learning"
      :pagination="
        filteredList.some(
          (item) => !['promo', 'favorites-promo'].includes(item.type)
        ) && filteredList.length > 1
      "
      :link="$router.resolve({ name: 'my-list' }).href"
      :link-text="viewAllText"
    >
      <cu-button-group
        slot="button-group"
        modal
        class="flex flex-wrap gap-2"
        multiselect
        grouprole="toolbar"
      >
        <cu-button
          v-for="filter in orderedFilters"
          :key="filter"
          @click="availableFilters.length !== 1 && handleFilters(filter)"
          :template="returnFilterBtnType(filter)"
          size="lg"
          :class="{ 'whitespace-nowrap': filter === 'in-progress' }"
          >{{ filterLabels[filter]
          }}<cu-icon-close-x
            v-if="returnFilterBtnX(filter)"
            data-icon
            size="s"
          ></cu-icon-close-x
        ></cu-button>
      </cu-button-group>

      <div
        v-for="(result, index) in filteredList"
        :key="result.id"
        slot="items"
        :style="
          ['promo', 'favorites-promo'].includes(result.type)
            ? '--card-width: 200px; --card-min-width: 200px'
            : ''
        "
      >
        <cu-promo-card
          v-if="result.type === 'promo'"
          heading="Select your interests"
          description="Choose your interests, and Cisco U. will show content you'll love. Until then, check out what's popular."
          btnText="Select interests"
          :style="`width: 200px; height: ${availableFilters.includes('bookmarks') ? '22rem' : '264px'}; --font-size-large: 14px; --card-min-height: 0`"
          @cu-promo-card="promoLink"
        ></cu-promo-card>
        <cu-base-card
          v-else-if="result.type == 'favorites-promo'"
          width="12.5rem"
          height="16.5rem"
          paddingTop="2rem"
          paddingBottom="2.75rem"
          paddingLeft="1.5rem"
          paddingRight="1.5rem"
          color="gray-0"
          rounded="10px"
        >
          <div class="flex flex-col items-left text-left px-[24px]">
            <h3 class="text-22 font-lighter leading-[1.2]">
              Save your Favorites
            </h3>
            <div class="bm-guide mt-4 flex items-center gap-2">
              <cu-bookmark displayOnly label="Bookmark"></cu-bookmark>
              <cu-icon-line-right
                class="text-gray"
                size="l"
              ></cu-icon-line-right>
              <cu-bookmark displayOnly class="active"></cu-bookmark>
            </div>
            <p class="mt-4 font-lighter leading-[1.25]">
              Content you bookmark or start will show up here.
            </p>
          </div>
        </cu-base-card>
        <the-cards
          v-else
          :result="resultData(result)"
          :for-carousel="true"
          :status="status"
          :is-removable="is_progressRemovable(result)"
          :is-admin-content="isAdminContent(result)"
          :index="index"
          :show-description="false"
          :card-view="'List'"
          :card-style="'Grid'"
        />
      </div>
    </cu-carousel>
  </div>
</template>

<style scoped>
.bm-guide cu-bookmark::part(bookmark) {
  width: 2rem;
  height: 2rem;
}

.bm-guide cu-bookmark::part(iconSize) {
  max-width: 1rem;
  max-height: 1rem;
}
</style>
